import { LockClosedIcon, PlusIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { DocumentTextIcon } from "@heroicons/react/24/solid";
import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Placeholder from "@tiptap/extension-placeholder";
import { Fragment } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { BubbleMenu, Editor, EditorContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReportTemplateContent } from "../../../db/schema";
import Button from "../../common/Button";
import Input from "../../common/Input";
import Tooltip from "../../common/Tooltip";
import { ReplaceText } from "../../Editor/ReplaceText";

// Common AI placeholders and instructions that users can insert
const PLACEHOLDER_PRESETS: {
  section: string;
  items: { label: string; description: string; value: string; raw?: boolean }[];
}[] = [
  {
    section: "Examples",
    items: [
      {
        label: "Case Header",
        description: "Case number, date, and author line",
        value:
          "Case Number: {{Case Number}}\nDate: {{The date I provide or if not provided, today's date}}\nAuthor: {{My Rank and Name}}",
        raw: true,
      },
      {
        label: "Dispatch & Arrival",
        description: "Dispatch and arrival times with location",
        value:
          "Dispatched: {{Dispatch Time}}\nArrived: {{Arrival Time}}\nLocation: {{Location or address of the incident}}",
        raw: true,
      },
      {
        label: "Officer Info Block",
        description: "Author, badge, and unit line",
        value:
          "Officer: {{My Rank and Name}}\nBadge #: {{My Badge Number}}\nUnit: {{My Unit or Division}}",
        raw: true,
      },
      {
        label: "SB 524 Disclaimer",
        description: "California AI disclosure requirement",
        value:
          "This report was written either fully or in part using artificial intelligence.",
        raw: true,
      },
      {
        label: "AI Assistance Disclaimer",
        description: "AI assistance disclosure with officer review note",
        value:
          "This narrative was prepared with the assistance of AI technology and is a product of the officer's own recollection unless otherwise noted. It has been reviewed for accuracy and completeness.",
        raw: true,
      },
      {
        label: "Person Info Block",
        description: "Format names with DOB, phone, address, email",
        value:
          "{{Every time you encounter a name in a report, write it out like this in a new paragraph:}}\n{{First Name}} {{Last Name}} {{Date of Birth}}\n{{Phone Number}}\n{{Address}}\n{{Email Address}}",
        raw: true,
      },
      {
        label: "Property/Vehicle Block",
        description: "Format property or vehicle references",
        value:
          "{{If property or a vehicle is referenced, write it out like this in a new paragraph:}}\n{{Property type or vehicle type}}\n{{Description including color and identifying marks}}\n{{Serial number / license plate / VIN if provided}}",
        raw: true,
      },
    ],
  },
  {
    section: "Case Details",
    items: [
      {
        label: "Case Number",
        description: "The case or incident number",
        value: "Case Number",
      },
      {
        label: "Incident Type",
        description: "Classification of the incident",
        value: "Incident Type",
      },
      {
        label: "Date",
        description: "Date of the incident or today's date",
        value:
          "The date I provide or if the date is not provided, today's date",
      },
      {
        label: "Time",
        description: "Time of the incident",
        value: "Time of the incident",
      },
      {
        label: "Location",
        description: "Location/address of incident",
        value: "Location or address of the incident",
      },
      {
        label: "Dispatch Time",
        description: "Time of dispatch",
        value: "Dispatch Time",
      },
      {
        label: "Arrival Time",
        description: "Time of officer arrival",
        value: "Arrival Time",
      },
      {
        label: "Departure Time",
        description: "Time officer cleared the scene",
        value: "Departure Time",
      },
    ],
  },
  {
    section: "Personnel",
    items: [
      {
        label: "Author",
        description: "Reporting officer's rank and name",
        value: "My Rank and Name",
      },
      {
        label: "Badge Number",
        description: "Officer's badge number",
        value: "My Badge Number",
      },
      {
        label: "Unit/Division",
        description: "Assigned unit or division",
        value: "My Unit or Division",
      },
      {
        label: "Partner/Backup",
        description: "Assisting officer's name",
        value: "Partner or Backup Officer Name",
      },
      {
        label: "Supervisor",
        description: "Approving supervisor's name",
        value: "Supervisor Name",
      },
    ],
  },
  {
    section: "Involved Parties",
    items: [
      {
        label: "Suspect Name",
        description: "Name of the suspect",
        value: "Suspect Name",
      },
      {
        label: "Suspect Description",
        description: "Physical description of suspect",
        value: "Physical description of the suspect",
      },
      {
        label: "Victim Name",
        description: "Name of the victim",
        value: "Victim Name",
      },
      {
        label: "Witness Name",
        description: "Name of the witness",
        value: "Witness Name",
      },
      {
        label: "Vehicle Description",
        description: "Description of vehicle involved",
        value: "Description of vehicle involved",
      },
      {
        label: "License Plate",
        description: "Vehicle license plate number",
        value: "License Plate Number",
      },
    ],
  },
  {
    section: "AI Instructions",
    items: [
      {
        label: "Topic Headings",
        description: "Structure the report with section headings",
        value:
          "Break up the report using topic headings where they apply: Synopsis, Dispatch, Arrival, Witness Statements, Participating Investigators, Evidence Collected, Conclusion",
      },
      {
        label: "Miranda Advisement",
        description: "Note when/if Miranda was read",
        value: "Include Miranda rights advisement if applicable",
      },
      {
        label: "Separate Witness Statements",
        description: "Organize witness accounts individually",
        value: "Summarize each witness statement separately",
      },
      {
        label: "Evidence & Chain of Custody",
        description: "Detail evidence collected and handling",
        value: "Include evidence description and chain of custody",
      },
    ],
  },
];

// ─── AI Placeholder Insert Menu ─────────────────────────────────────────────
interface PlaceholderMenuProps {
  editor: Editor | null;
}

function PlaceholderMenu({ editor }: PlaceholderMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [customPlaceholder, setCustomPlaceholder] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  const insert = useCallback(
    (value: string, raw = false) => {
      if (editor) {
        editor
          .chain()
          .focus()
          .insertContent(raw ? value : `{{${value}}}`)
          .run();
      }
      setShowMenu(false);
      setCustomPlaceholder("");
    },
    [editor],
  );

  return (
    <div className="relative" ref={menuRef} data-tour="template-insert">
      <Tooltip
        overlay={
          <span>
            Select text and press{" "}
            <kbd className="px-1 py-0.5 bg-white/20 rounded text-[11px] font-mono">
              {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "\u2318" : "Ctrl"}
              +/
            </kbd>{" "}
            to wrap as placeholder
          </span>
        }
        placement="top"
      >
        <Button
          variant="outline"
          size="xs"
          onClick={() => setShowMenu(!showMenu)}
          className="border-amber-200 dark:border-amber-900 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:border-amber-200 dark:hover:border-amber-900 whitespace-nowrap"
        >
          <PlusIcon className="w-3 h-3 shrink-0" />
          <span className="hidden lg:inline">
            Insert AI Placeholder / Instruction
          </span>
          <span className="hidden sm:inline lg:hidden">
            Insert AI Placeholder
          </span>
          <span className="sm:hidden">Insert</span>
        </Button>
      </Tooltip>

      {showMenu && (
        <div className="absolute top-full right-0 mt-1 z-50 w-72 bg-base-100 rounded-lg shadow-lg border border-base-300 overflow-hidden">
          <div className="p-2 border-b border-slate-100 dark:border-base-300">
            <div className="flex items-center gap-1.5">
              <Input
                variant="sm"
                value={customPlaceholder}
                onChange={(e) => setCustomPlaceholder(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customPlaceholder.trim()) {
                    insert(customPlaceholder.trim());
                  }
                }}
                placeholder="Type custom AI placeholder..."
                className="flex-1 !max-w-none"
                autoFocus
              />
              <Button
                variant="primary"
                size="xs"
                onClick={() => {
                  if (customPlaceholder.trim()) {
                    insert(customPlaceholder.trim());
                  }
                }}
                disabled={!customPlaceholder.trim()}
                className="!bg-amber-500 hover:!bg-amber-600"
              >
                Add
              </Button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {PLACEHOLDER_PRESETS.map((group) => (
              <div key={group.section}>
                <div className="px-3 py-1.5 bg-base-200 border-y border-slate-100 dark:border-base-300">
                  <p className="text-[10px] font-semibold text-slate-400 dark:text-base-content/40 uppercase tracking-wider">
                    {group.section}
                  </p>
                </div>
                {group.items.map((item) => (
                  <button
                    key={item.label}
                    data-placeholder-item={item.label}
                    onClick={() => insert(item.value, item.raw)}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors flex items-center justify-between group/item"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-base-content/80 my-1">
                        {item.label}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-base-content/40 my-1">
                        {item.description}
                      </p>
                    </div>
                    <PlusIcon className="w-3.5 h-3.5 text-slate-300 dark:text-base-content/30 group-hover/item:text-amber-500 shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── How it works info ────────────────────────────────────────────────────────
function HowTemplatesWork() {
  return (
    <div className="rounded-xl border border-base-300 bg-base-200/50 p-5">
      <h4 className="text-xs font-semibold text-slate-500 dark:text-base-content/60 uppercase tracking-wider mb-3 mt-2">
        How Templates Work
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="w-4 h-4 text-slate-400 dark:text-base-content/40 mt-1" />
            <p className="text-xs font-medium text-slate-600 dark:text-base-content/70 mb-2">
              Plain Text
            </p>
          </div>
          <p className="text-xs text-slate-400 dark:text-base-content/40 leading-relaxed">
            Everything you type appears verbatim in the report.
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-4 h-4 text-amber-400 mt-1" />
            <p className="text-xs font-medium text-slate-600 dark:text-base-content/70 mb-2">
              AI Placeholder / Instruction
            </p>
          </div>
          <p className="text-xs text-slate-400 dark:text-base-content/40 leading-relaxed">
            The AI replaces these with content from your audio. Use for specific
            fields like <b>Case Number</b> or full instructions like{" "}
            <b>Use topic headings for each section</b>.
          </p>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4 text-amber-400 mt-1" />
            <p className="text-xs font-medium text-slate-600 dark:text-base-content/70 mb-2">
              Bracket Notation
            </p>
          </div>
          <p className="text-xs text-slate-400 dark:text-base-content/40 leading-relaxed">
            Type{" "}
            <code className="px-1 py-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 rounded text-[11px]">
              {"{{placeholder}}"}
            </code>{" "}
            directly, or select text and press{" "}
            <kbd className="px-1 py-0.5 bg-slate-100 dark:bg-base-300 rounded text-[11px] text-slate-500 dark:text-base-content/60 font-mono">
              {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "\u2318" : "Ctrl"}
              +/
            </kbd>{" "}
            to wrap it as an AI placeholder.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Wrap selection in {{ }} or start empty placeholder ──────────────────────
function wrapSelectionInBraces(editor: Editor) {
  const { from, to } = editor.state.selection;
  const selectedText = editor.state.doc.textBetween(from, to, "\n");
  const { tr } = editor.state;
  const schema = editor.schema;

  if (selectedText) {
    const lines = selectedText.split("\n");
    const hasLineBreaks = lines.length > 1 && schema.nodes.hardBreak;

    if (!hasLineBreaks) {
      const wrapped = `{{${selectedText}}} `;
      tr.insertText(wrapped, from, to);
      tr.setSelection(TextSelection.create(tr.doc, from + wrapped.length));
    } else {
      // Build inline fragment: {{ + text with hard breaks + }} + space
      const nodes: any[] = [schema.text("{{")];
      lines.forEach((line, i) => {
        if (i > 0) nodes.push(schema.nodes.hardBreak.create());
        if (line) nodes.push(schema.text(line));
      });
      nodes.push(schema.text("}} "));

      tr.replaceWith(from, to, Fragment.from(nodes));
      const endPos =
        from + nodes.reduce((sum: number, n: any) => sum + n.nodeSize, 0);
      tr.setSelection(TextSelection.create(tr.doc, endPos));
    }
  } else {
    tr.insertText("{{ }}", from, to);
    tr.setSelection(TextSelection.create(tr.doc, from + 2, from + 3));
  }

  editor.view.dispatch(tr);
  editor.view.focus();
  return true;
}

// ─── Section Editor: single TipTap editor per section ────────────────────────
const SECTION_CHAR_LIMIT = 2000;

interface SectionEditorProps {
  title: string;
  description: string;
  content: string;
  onChange: (content: string) => void;
  placeholder: string;
  readOnly?: boolean;
}

function SectionEditor({
  title,
  description,
  content,
  onChange,
  placeholder,
  readOnly,
}: SectionEditorProps) {
  const [charCount, setCharCount] = useState(content.replace(/<br>/g, "\n").length);
  const initialHtml = content
    ? content
        .split("\n")
        .map((line) => `<p>${line === "<br>" ? "" : line}</p>`)
        .join("")
    : "";

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      Document,
      HardBreak,
      History,
      Paragraph,
      Text,
      Placeholder.configure({ placeholder }),
      ReplaceText.configure({ selectOnClick: false }),
      Extension.create({
        name: "wrapInBraces",
        addKeyboardShortcuts() {
          // Jump cursor past }} if positioned right before closing braces
          const jumpPastClosingBraces = (e: Editor): boolean => {
            const { from, to } = e.state.selection;
            if (from !== to) return false;
            const $pos = e.state.doc.resolve(from);
            const parentText = $pos.parent.textContent;
            const offset = $pos.parentOffset;
            const textBefore = parentText.slice(0, offset);
            const textAfter = parentText.slice(offset);

            if (textAfter.startsWith("}}") && textBefore.includes("{{")) {
              const tr = e.state.tr;
              const endPos = from + 2;
              const parentEnd = $pos.start() + $pos.parent.content.size;
              if (endPos >= parentEnd) {
                tr.insertText(" ", endPos);
              }
              tr.setSelection(TextSelection.create(tr.doc, endPos));
              e.view.dispatch(tr);
              return true;
            }
            return false;
          };

          return {
            "Mod-/": ({ editor: e }) => wrapSelectionInBraces(e),
            Enter: ({ editor: e }) => jumpPastClosingBraces(e),
            ArrowRight: ({ editor: e }) => jumpPastClosingBraces(e),
          };
        },
      }),
    ],
    content: initialHtml,
    onUpdate({ editor: e }) {
      const doc = e.state.doc;
      const lines: string[] = [];
      doc.forEach((node) => {
        let text = "";
        node.forEach((child) => {
          if (child.type.name === "hardBreak") {
            text += "<br>";
          } else {
            text += child.text || "";
          }
        });
        lines.push(text.replace(/<br>/g, "").trim() ? text : "");
      });
      const plain = lines.join("\n");
      setCharCount(plain.replace(/<br>/g, "\n").length);
      onChange(plain);
    },
    editorProps: {
      attributes: {
        class: `template-tiptap-editor w-full rounded-b-xl px-4 py-3 text-sm leading-relaxed transition-colors outline-none ${readOnly ? "bg-base-200 text-slate-400 dark:text-base-content/40 cursor-not-allowed" : "bg-base-100 text-slate-700 dark:text-base-content/80"}`,
        style: "min-height: 80px",
      },
      handleTextInput(view) {
        return view.state.doc.textContent.length >= SECTION_CHAR_LIMIT;
      },
      handlePaste(view, _event, slice) {
        const currentLen = view.state.doc.textContent.length;
        const pasteLen = slice.content.textBetween(0, slice.content.size, "\n").length;
        return currentLen + pasteLen > SECTION_CHAR_LIMIT;
      },
    },
  });

  return (
    <>
      <div className="rounded-xl border border-base-300">
        {/* Section header */}
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-base-200 border-b border-base-300 rounded-t-xl">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-base-content/80 my-2">{title}</h3>
            <p className="text-xs text-slate-400 dark:text-base-content/40 mt-0.5">{description}</p>
          </div>
          {readOnly ? (
            <div className="shrink-0 flex items-center gap-1.5 text-slate-400 dark:text-base-content/40">
              <LockClosedIcon className="w-3.5 h-3.5" />
              <span className="text-xs">Managed by department</span>
            </div>
          ) : (
            <div className="shrink-0">
              <PlaceholderMenu editor={editor} />
            </div>
          )}
        </div>

        {/* Bubble menu on selection */}
        {editor && !readOnly && (
          <BubbleMenu
            editor={editor}
            tippyOptions={{ placement: "top", duration: 150 }}
            shouldShow={({ editor: e }) => {
              const { from, to } = e.state.selection;
              if (from === to) return false;
              // Hide if selected text contains a placeholder
              const selectedText = e.state.doc.textBetween(from, to, "\n");
              if (/\{\{|\}\}/.test(selectedText)) return false;
              // Hide if selection is inside a placeholder
              const $from = e.state.doc.resolve(from);
              const $to = e.state.doc.resolve(to);
              if ($from.parent === $to.parent) {
                const fullText = $from.parent.textContent;
                const startOffset = $from.parentOffset;
                const endOffset = $to.parentOffset;
                const braceRegex = /\{\{[^}]*\}\}/g;
                let match;
                while ((match = braceRegex.exec(fullText)) !== null) {
                  if (
                    startOffset < match.index + match[0].length &&
                    endOffset > match.index
                  ) {
                    return false;
                  }
                }
              }
              return true;
            }}
          >
            <Button
              variant="outline"
              size="xs"
              onClick={() => wrapSelectionInBraces(editor)}
              className="shadow-md text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 bg-base-100"
            >
              <SparklesIcon className="w-3.5 h-3.5" />
              Make AI Placeholder
              <kbd className="ml-1 px-1 py-0.5 rounded bg-slate-100 dark:bg-base-200 text-[10px] text-slate-400 dark:text-base-content/40 font-mono">
                {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "\u2318" : "Ctrl"}/
              </kbd>
            </Button>
          </BubbleMenu>
        )}

        {/* Editor */}
        <EditorContent editor={editor} />
      </div>

      {/* Character count */}
      {!readOnly && (
        <div className="flex justify-end !mt-0 pr-1">
          <span className={`text-[11px] ${charCount >= SECTION_CHAR_LIMIT ? "text-red-500 font-medium" : "text-slate-400 dark:text-base-content/40"}`}>
            {charCount} / {SECTION_CHAR_LIMIT}
          </span>
        </div>
      )}
    </>
  );
}

// ─── Main Template Editor ────────────────────────────────────────────────────
interface ReportTemplateEditorProps {
  template?: {
    id: string;
    name: string;
    description: string | null;
    content: ReportTemplateContent;
    is_default: boolean;
  };
  /** Called on every content change so parent can track it */
  onContentChange?: (content: ReportTemplateContent) => void;
  /** When true, the editor is non-editable (e.g. agency templates in officer view) */
  readOnly?: boolean;
}

export default function ReportTemplateEditor({
  template,
  onContentChange,
  readOnly,
}: ReportTemplateEditorProps) {
  const [content, setContent] = useState<ReportTemplateContent>(
    template?.content || { header: "", footer: "", body: "" },
  );

  const updateContent = (newContent: ReportTemplateContent) => {
    setContent(newContent);
    onContentChange?.(newContent);
  };

  return (
    <div className="space-y-4">
      <div data-tour="template-header">
        <SectionEditor
          title="Custom Header"
          description="Add a boilerplate header to the beginning of all reports."
          content={content.header}
          onChange={(header) => updateContent({ ...content, header })}
          placeholder="e.g., Case #: {{Case Number}}  Date: {{Date}}  Reporting Officer: {{Officer Name}}"
          readOnly={readOnly}
        />
      </div>

      <div data-tour="template-body">
        <SectionEditor
          title="Narrative Writing Instructions"
          description="Add custom writing rules for your report narrative. Use this for structure, articulation detail, formatting, or other guidance not addressed by the preferences above."
          content={content.body}
          onChange={(body) => updateContent({ ...content, body })}
          placeholder="e.g., {{Break up the report using topic headings: SYNOPSIS, DISPATCH, ARRIVAL, INVESTIGATION, CONCLUSION}}"
          readOnly={readOnly}
        />
      </div>

      <div data-tour="template-footer">
        <SectionEditor
          title="Custom Footer"
          description="Add a boilerplate footer to the end of all reports."
          content={content.footer}
          onChange={(footer) => updateContent({ ...content, footer })}
          placeholder="e.g., This narrative was prepared with the assistance of AI technology."
          readOnly={readOnly}
        />
      </div>

      <HowTemplatesWork />
    </div>
  );
}
